// Normalization, duplicate resolution and review of a catalog import (Phase 3.4). This file holds the
// FROZEN contract: the codes and the shapes that the database CHECK constraints, the staff API schemas
// and the review screen share. `@lafayette/schemas` repeats the tuples (a parity test in `apps/api`
// fails when one differs). Later changes are additive only.
//
// Plain words: the validator reads each raw row, cleans it with fixed rules, and writes a "proposal v2"
// (one tidy value per field, with the rule that made it). It then looks for items that already exist.
// Anything the rules cannot settle (a `$` price, a guessed time zone, a weak duplicate, a destructive
// update, a taxonomy word nobody knows) becomes a REQUIRED DECISION that a catalog editor makes.
// Decisions are append-only rows. They never change the proposal.
/** The version of the normalizer rules, stored per validation run. A rule change raises it. */
export const NORMALIZER_VERSION = 1;
/** `import_rows.proposal_schema_version` of a proposal written by the validator. 707 writes 1. */
export const IMPORT_PROPOSAL_SCHEMA_VERSION_V2 = 2;
export const IMPORT_MAX_REVIEW_DECISIONS = 500;
export const IMPORT_MAX_DUPLICATE_CANDIDATES = 5;
export const IMPORT_MAX_PEOPLE_PER_ITEM = 20;
/** A bucket of items with the same type, country and locality larger than this is not searched. */
export const IMPORT_MAX_MATCH_BUCKET = 500;
export const IMPORT_ASSIST_MAX_CALLS_LIMIT = 500;
export const IMPORT_ASSIST_DEFAULT_MAX_CALLS = 100;
/** A model answer at or above this confidence (basis points) is used without a staff decision. */
export const IMPORT_ASSIST_ACCEPT_BP = 9000;
// --- run settings ---------------------------------------------------------------------------------------
/** How a numeric date such as `03/04/2027` is read. `ISO_ONLY` refuses an ambiguous one. */
export const importDateOrders = Object.freeze(["ISO_ONLY", "DMY", "MDY"]);
/** `OFF` is the production default. `FAKE` is for tests and development. `PROVIDER` is refused until an adapter exists. */
export const importAssistModes = Object.freeze(["OFF", "FAKE", "PROVIDER"]);
export const IMPORT_DEFAULT_SOURCE_SYSTEM = "catalog_import";
// --- decisions ------------------------------------------------------------------------------------------
export const importDecisionKinds = Object.freeze([
    "CURRENCY",
    "TIME_ZONE",
    // Reserved: an ambiguous numeric date is an ERROR in 708 (the row is invalid), so no row requires it.
    "DATE_ORDER",
    "TAXONOMY",
    "DUPLICATE_ACTION",
    "DESTRUCTIVE_UPDATE",
]);
/** `chosenCode` of a `DUPLICATE_ACTION`. `LINK_AS_UPDATE` names one candidate item. */
export const importDuplicateActions = Object.freeze([
    "LINK_AS_UPDATE",
    "CREATE_NEW",
    "SKIP",
]);
/** `chosenCode` of a `CURRENCY` decision that keeps the price as text: the price becomes UNKNOWN and the text an internal note. */
export const IMPORT_KEEP_AS_TEXT = "KEEP_AS_TEXT";
// --- bulk review ----------------------------------------------------------------------------------------
/**
 * Which rows a bulk review takes.
 * - `READY_AND_RESOLVED`: READY rows, WARNING rows with no open decision, and DUPLICATE_CANDIDATE rows with
 *   a STRONG single match and no open decision (the proposed action is applied).
 * - `STRONG_UNCHANGED`: STRONG matches whose update changes nothing. Only SKIPPED.
 * - `INVALID`: INVALID rows. Only SKIPPED.
 * A WEAK match (name and place, or fuzzy) is never taken by a bulk review.
 */
export const importBulkSelectors = Object.freeze([
    "READY_AND_RESOLVED",
    "STRONG_UNCHANGED",
    "INVALID",
]);
export const importReviewOutcomes = Object.freeze(["APPROVED", "SKIPPED"]);
/** Why a row was not changed by a review. */
export const importReviewRefusalCodes = Object.freeze([
    "ROW_NOT_FOUND",
    "ROW_STATE",
    "DECISION_MISSING",
    "INVALID_ROW",
    "CANDIDATE_UNKNOWN",
]);
/** Why a job cannot be approved yet. */
export const importApprovalBlockerCodes = Object.freeze([
    "ROWS_UNDECIDED",
    "NO_APPROVED_ROWS",
    "DECISIONS_OPEN",
]);
// --- duplicates -----------------------------------------------------------------------------------------
/** STRONG: an identifier matched (provider id, source key). WEAK: name and place, or a fuzzy match. */
export const importMatchStrengths = Object.freeze(["STRONG", "WEAK"]);
// --- normalization --------------------------------------------------------------------------------------
/** How a field value was made. A code, never text. */
export const importNormalizationRules = Object.freeze([
    "CLEANED",
    "PARSED",
    "DATE_PARTS_JOINED",
    "CODE_MATCH",
    "LABEL_MATCH",
    "SYNONYM",
    "MODEL_ASSISTED",
    "CURRENCY_CODE",
    "CURRENCY_SYMBOL",
    "CURRENCY_COLUMN",
    "SPLIT_LIST",
    "COUNTRY_LOOKUP",
    "URL_NORMALIZED",
    "DEFAULT",
]);
/** Where the input of a field came from. */
export const importFieldSources = Object.freeze([
    "CELL",
    "CELLS",
    "JOB_SETTING",
    "CITY_LOOKUP",
    "MODEL",
    "ITEM_TYPE_DEFAULT",
]);
/** Where an event's time zone came from. `CITY_LOOKUP` is a suggestion that needs a `TIME_ZONE` decision. */
export const importZoneSources = Object.freeze(["COLUMN", "JOB_DEFAULT", "CITY_LOOKUP"]);
// --- processing -----------------------------------------------------------------------------------------
export const importProcessingResults = Object.freeze([
    "SUCCEEDED",
    "FAILED_RETRYABLE",
    "FAILED_FINAL",
]);
export const importProcessingErrorCodes = Object.freeze([
    "ITEM_CHANGED_SINCE_REVIEW",
    "DECISION_MISSING",
    "DESTRUCTIVE_NOT_CONFIRMED",
    "SOURCE_TAKEN",
    "TAXONOMY_MISSING",
    "ITEM_NOT_EDITABLE",
    "CONSTRAINT",
    "LOCK_TIMEOUT",
    "INTERNAL",
]);
export const IMPORT_PROCESSING_MAX_ATTEMPTS = 10;
/** A row that fails with a retryable error is tried this many times before it fails for good. */
export const IMPORT_PROCESSING_RETRYABLE_LIMIT = 3;
/**
 * What staff see for a row that was approved or finished. `WAITING`: approved, no attempt yet.
 * `RETRYING`: the last attempt failed and another will run. `FAILED`: it failed for good (the job fails
 * and staff retry). `CREATED` and `UPDATED`: imported. `SKIPPED`: staff skipped it.
 */
export const importProcessingStates = Object.freeze([
    "WAITING",
    "RETRYING",
    "FAILED",
    "CREATED",
    "UPDATED",
    "SKIPPED",
]);
/** The system actors that may write catalog rows for an import. */
export const CATALOG_IMPORT_WRITERS = Object.freeze(["import-processor"]);
